import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DepartmentDTO } from '../../shared/models/departmentDTO';
import { DepartmentApiService } from 'app/core/api/services/department-api.service';

interface CacheEntry {
  subject: BehaviorSubject<DepartmentDTO | null>;
  expiresAt: number;
  inFlight?: Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class DepartmentCacheService {
  private cache = new Map<string, CacheEntry>();
  private readonly ttlMs: number = 60_000; // 60s TTL; can be made configurable via environment

  constructor(private readonly departmentApi: DepartmentApiService) {
    if (typeof window !== 'undefined' && window?.addEventListener) {
      window.addEventListener('focus', () => this.revalidateExpired());
    }
  }

  getDepartment$(departmentId: string, forceRefresh: boolean = false): Observable<DepartmentDTO | null> {
    if (!departmentId) {
      return new BehaviorSubject<DepartmentDTO | null>(null).asObservable();
    }

    const entry = this.ensureEntry(departmentId);
    const now = Date.now();

    if (forceRefresh || now > entry.expiresAt) {
      this.refresh(departmentId, entry);
    }

    return entry.subject.asObservable();
  }

  updateFromMutation(departmentId: string, updated: DepartmentDTO): void {
    const entry = this.ensureEntry(departmentId);
    entry.subject.next(updated);
    entry.expiresAt = Date.now() + this.ttlMs;
  }

  invalidate(departmentId: string): void {
    this.cache.delete(departmentId);
  }

  private ensureEntry(departmentId: string): CacheEntry {
    let entry = this.cache.get(departmentId);
    if (!entry) {
      entry = {
        subject: new BehaviorSubject<DepartmentDTO | null>(null),
        expiresAt: 0,
      };
      this.cache.set(departmentId, entry);
    }
    return entry;
  }

  private async refresh(departmentId: string, entry: CacheEntry): Promise<void> {
    if (entry.inFlight) return;

    entry.inFlight = (async () => {
      try {
        await new Promise<void>((resolve) => {
          this.departmentApi.getDepartmentbyId(departmentId).subscribe({
            next: (dep: DepartmentDTO) => {
              entry.subject.next(dep);
              entry.expiresAt = Date.now() + this.ttlMs;
              resolve();
            },
            error: () => {
              // keep old cached value, just shorten ttl for retry
              entry.expiresAt = Date.now() + Math.min(10_000, this.ttlMs / 2);
              resolve();
            },
          });
        });
      } finally {
        entry.inFlight = undefined;
      }
    })();
  }

  private revalidateExpired(): void {
    const now = Date.now();
    for (const [id, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.refresh(id, entry);
      }
    }
  }
}
